// [eyecue-codemap:7Q7iHcxkiRN]

import {
  debounce,
  defer,
  firstValueFrom,
  from,
  Observable,
  of,
  timeout,
  timer,
} from "rxjs"

import { backoff } from "./backoff"
import { parseSeconds } from "./parse-seconds"
import type { SseMessage } from "./sse-parser"
import { SseParser } from "./sse-parser"

// [eyecue-codemap:Eumt28WWUVP]
export interface NotifierConnected {
  type: `CONNECTED`
  messagesPossiblyMissed: boolean // [eyecue-codemap:sZrTDeKGaD]
}

// [eyecue-codemap:8NLFJNf473H]
export interface NotifierMessage {
  type: `MESSAGE`
  message: SseMessage
}

// [eyecue-codemap:P7Yid1usuFY]
export interface NotifierDisconnected {
  type: `DISCONNECTED`
  reconnectDelaySec: number
  messagesPossiblyMissed: boolean // [eyecue-codemap:VTPWsE16evV]
}

export type NotifierEvent =
  | NotifierConnected
  | NotifierDisconnected
  | NotifierMessage

class PermanentFailure extends Error {}

export interface NotifierChannelObservableConfig {
  channelNameSpec: string
  jwt?: string
  messageDebounceSeconds?: number
  sseUrl: string
}

// [eyecue-codemap:ABRw884PCSP]
export function observeNotifier(
  config: NotifierChannelObservableConfig,
): Observable<NotifierEvent> {
  return defer(() => {
    let lastEventID: string | undefined
    let reconnectDelayMs = 0
    let lastReceivedTime: Date
    let streamMaxSeconds: number

    function messagesPossiblyMissed() {
      if (!lastReceivedTime) {
        return true
      }

      if (!streamMaxSeconds) {
        throw Error(`BUG: should never happen`)
      }

      return (Date.now() - lastReceivedTime.valueOf()) / 1000 > streamMaxSeconds
    }

    const unsubscribeAbort = new AbortController()

    const generator = async function* (): AsyncIterable<NotifierEvent> {
      while (true) {
        if (reconnectDelayMs > 0) {
          yield {
            type: `DISCONNECTED`,
            reconnectDelaySec: reconnectDelayMs / 1000,
            messagesPossiblyMissed: messagesPossiblyMissed(),
          }

          await new Promise((r) => setTimeout(r, reconnectDelayMs))

          if (unsubscribeAbort.signal.aborted) {
            return
          }
        }

        const timeoutAbortController = new AbortController()

        function handleUnsubscribeAbort() {
          timeoutAbortController.abort()
        }

        unsubscribeAbort.signal.addEventListener(`abort`, handleUnsubscribeAbort)

        try {
          const req = new Request(
            config.sseUrl + `?ch=` + encodeURIComponent(config.channelNameSpec),
          )
          if (lastEventID) {
            req.headers.set(`Last-Event-ID`, lastEventID)
          }
          if (config.jwt) {
            req.headers.set(`Authorization`, `Bearer ` + config.jwt)
          }

          const resp = await fetch(req, {
            signal: timeoutAbortController.signal,
            credentials: "include",
          })

          if (!resp.ok) {
            const bodyText = await resp.text()
            const errorMsg = `HTTP status ${resp.status} ${resp.statusText}: ${bodyText}`
            if (resp.status >= 400 && resp.status <= 499) {
              throw new PermanentFailure(errorMsg)
            } else {
              throw Error(errorMsg)
            }
          }

          const heartbeatIntervalSeconds = parseSeconds(
            resp.headers.get(`X-Notifier-Heartbeat-Seconds`),
          )

          streamMaxSeconds = parseSeconds(
            resp.headers.get(`X-Notifier-Stream-Max-Seconds`),
          )

          yield {
            type: `CONNECTED`,
            messagesPossiblyMissed: messagesPossiblyMissed(),
          }

          lastReceivedTime = new Date()
          reconnectDelayMs = 0

          const sseParser = new SseParser()

          if (!resp.body) throw new Error(`BUG: no body`)
          const reader = resp.body
            .pipeThrough(new TextDecoderStream())
            .getReader()

          while (true) {
            const readResult = await firstValueFrom(
              from(reader.read()).pipe(
                timeout((2 * heartbeatIntervalSeconds + 10) * 1000),
              ), // timeout if we miss 2 heartbeats
            )

            if (readResult.done) {
              break
            }

            lastReceivedTime = new Date()

            for (const message of sseParser.handleChunk(readResult.value)) {
              yield {
                type: `MESSAGE`,
                message: message,
              }

              lastEventID = message.id
            }
          }
        } catch (e) {
          if (e instanceof PermanentFailure) {
            throw e
          }
          console.warn(e)
          reconnectDelayMs = backoff(reconnectDelayMs)
        } finally {
          timeoutAbortController.abort()
          unsubscribeAbort.signal.removeEventListener(
            `abort`,
            handleUnsubscribeAbort,
          )
        }
      }
    }

    let observable = new Observable<NotifierEvent>((subscriber) => {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;(async function () {
        try {
          for await (const value of generator()) {
            subscriber.next(value)
            if (subscriber.closed) {
              return
            }
          }

          throw Error(`BUG: observable should never complete`)
        } catch (e) {
          subscriber.error(e)
        }
      })()

      return () => unsubscribeAbort.abort()
    })

    if (config.messageDebounceSeconds) {
      const debounceMs = config.messageDebounceSeconds * 1000
      observable = observable.pipe(
        debounce((evt) =>
          evt.type === `MESSAGE` ? timer(debounceMs) : of(null),
        ),
      )
    }

    return observable
  })
}
